import { useIsFetching } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const HAS_EXPLICIT_TZ = /([zZ]|[+-]\d{2}:?\d{2})$/;

/**
 * Parse API timestamps that are UTC wall times without a zone, e.g.
 * `2026-07-27T15:21:05.461000` or `2026-07-24T00:00:00`.
 * Naive strings are treated as UTC; `Z` / offset strings are used as-is.
 * Returns absolute epoch ms (timezone-safe for countdown math across OS TZs).
 */
export const parseUtcTimestampMs = (
  timestamp: string | null | undefined,
): number | null => {
  if (!timestamp) return null;

  const trimmed = timestamp.trim();
  if (!trimmed) return null;

  const normalized = trimmed.includes('T')
    ? trimmed
    : trimmed.replace(' ', 'T');

  const asUtc = HAS_EXPLICIT_TZ.test(normalized)
    ? normalized
    : `${normalized}Z`;

  const parsed = dayjs.utc(asUtc);
  if (!parsed.isValid()) return null;
  return parsed.valueOf();
};

/**
 * Format a UTC-naive (or zoned) API timestamp in the viewer's local timezone.
 */
export const formatUtcTimestampLocalTime = (
  timestamp: string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string | null => {
  const ms = parseUtcTimestampMs(timestamp);
  if (ms == null) return null;

  return new Date(ms).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    ...options,
  });
};

/**
 * Helper function to determine if a given date range matches any of the predefined standard date ranges.
 * Standard ranges are calculated relative to today's date and include common periods like current day,
 * 7 days, 30 days, and 90 days. This function is useful for filtering out standard ranges from
 * custom date range lists to avoid duplication in the UI.
 *
 * @param startDate - The start date of the range to check (YYYY-MM-DD format)
 * @param endDate - The end date of the range to check (YYYY-MM-DD format)
 * @param standardRanges - Optional array of standard ranges to check against. If not provided, uses default ranges.
 * @returns true if the date range matches any standard range, false otherwise
 */
export const isStandardDateRange = (
  startDate: string,
  endDate: string,
  standardDays?: number[],
): boolean => {
  const today = dayjs().format('YYYY-MM-DD');

  // Default standard ranges if none provided
  const defaultDays = [
    0, // Current day
    6, // 7 days (subtract 6, include today)
    29, // 30 days (subtract 29, include today)
    89, // 90 days (subtract 89, include today)
  ];

  const days = standardDays || defaultDays;

  return days.some((dayCount) => {
    const expectedStart =
      dayCount === 0
        ? today
        : dayjs().subtract(dayCount, 'day').format('YYYY-MM-DD');
    const expectedEnd = today;

    return startDate === expectedStart && endDate === expectedEnd;
  });
};

// Inclusive day count helper
export const getInclusiveDays = (
  startDate: string | Dayjs,
  endDate: string | Dayjs,
) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  return end.diff(start, 'day') + 1;
};

/**
 * Helper function to find an appropriate start date from available dates.
 * Attempts to find a date that is a specified number of days before the end date.
 * If the exact target date is not available, it finds the closest available date.
 *
 * @param availableDates - Array of available dates in YYYY-MM-DD format
 * @param daysBack - Number of days to subtract from end date (default: 89)
 * @param endDate - The end date to calculate from (default: today)
 * @returns Dayjs object representing the best available start date
 */
export const findInitialStartDate = (
  availableDates: string[],
  daysBack: number = 60,
  endDate: Dayjs = dayjs(),
): Dayjs => {
  if (availableDates.length === 0) {
    return endDate.subtract(daysBack, 'days');
  }

  const targetStartDate = endDate.subtract(daysBack, 'days');
  const targetDateString = targetStartDate.format('YYYY-MM-DD');

  // If the exact target date is available, use it
  if (availableDates.includes(targetDateString)) {
    return targetStartDate;
  }

  // Find the most recent available date before the target date
  const datesBeforeTarget = availableDates
    .map((date) => dayjs(date))
    .filter((date) => date.isBefore(targetStartDate))
    .sort((a, b) => b.diff(a)); // Sort descending (most recent first)

  if (datesBeforeTarget.length > 0) {
    return datesBeforeTarget[0];
  }

  // If no dates before target, use the earliest available date
  const earliestDate = availableDates
    .map((date) => dayjs(date))
    .sort((a, b) => a.diff(b))[0]; // Sort ascending (earliest first)

  return earliestDate;
};

/**
 * Yesterday, or the most recent weekday if yesterday falls on a weekend.
 * Returns `YYYY-MM-DD`.
 */
export const getPreviousWeekday = (from: Dayjs = dayjs()): string => {
  let d = from.subtract(1, 'day');
  while (d.day() === 0 || d.day() === 6) {
    d = d.subtract(1, 'day');
  }
  return d.format('YYYY-MM-DD');
};

/**
 * Hook to check if a custom date range query is currently loading.
 * Only shows loading for custom ranges, not standard ones.
 *
 * @param queryKey - The base query key to check (default: 'dashboard-data-v2')
 * @returns boolean indicating if a custom range query is loading
 */
export const useIsCustomRangeLoading = (
  queryKey: string = 'dashboard-data-v2',
): boolean => {
  return (
    useIsFetching({
      predicate: (query) => {
        const key = query.queryKey as readonly unknown[];
        if (!Array.isArray(key) || key[0] !== queryKey || !key[1] || !key[2]) {
          return false;
        }

        const startDate = key[1] as string;
        const endDate = key[2] as string;

        // Only show loading for custom ranges, not standard ones
        return !isStandardDateRange(startDate, endDate);
      },
    }) > 0
  );
};

/**
 * Hook to check if the currently active date range query is loading.
 * Only shows loading when the specific active range is being fetched.
 *
 * @param activeRange - The currently active date range
 * @param sections - The sections being queried (not used in v2 API)
 * @param queryKey - The base query key to check (default: 'dashboard-data-v2')
 * @returns boolean indicating if the active range query is loading
 */
export const useIsActiveRangeLoading = (
  activeRange: { startDate: string; endDate: string } | null,
  sections?: any[] | null,
  queryKey: string = 'dashboard-data-v2',
): boolean => {
  return (
    useIsFetching({
      predicate: (query) => {
        const key = query.queryKey as readonly unknown[];
        if (!Array.isArray(key) || key[0] !== queryKey || !key[1] || !key[2]) {
          return false;
        }

        const startDate = key[1] as string;
        const endDate = key[2] as string;

        // Only show loading if this is the exact active range being queried
        return !!(
          activeRange &&
          startDate === activeRange.startDate &&
          endDate === activeRange.endDate
        );
      },
    }) > 0
  );
};
